import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'EPC commercial request failed');
  return body.data;
}

export type EpcCommercialDashboard = {
  tendersByStatus: Array<{ status: string; cnt: number | string }>;
  awardsByStatus: Array<{ status: string; cnt: number | string }>;
  contractsByStatus: Array<{ status: string; cnt: number | string }>;
  mobilizationsByStatus: Array<{ status: string; cnt: number | string }>;
  openRiskCount: number;
};

export type EpcTenderWorkspace = {
  tender: Record<string, unknown>;
  bidDecisions: Record<string, unknown>[];
  proposals: Record<string, unknown>[];
  submissions: Record<string, unknown>[];
  clarifications: Record<string, unknown>[];
  addenda: Record<string, unknown>[];
  negotiations: Record<string, unknown>[];
  risks: Record<string, unknown>[];
  awards: Record<string, unknown>[];
};

export const epcCommercialService = {
  getRuntime: () => unwrap<Record<string, unknown>>(api.get('/epc/commercial/runtime')),

  getDashboard: () => unwrap<EpcCommercialDashboard>(api.get('/epc/commercial/dashboard')),

  listPrequalifications: (opportunityId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/prequalifications', {
        params: opportunityId ? { opportunityId } : {},
      })
    ),
  getPrequalification: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/prequalifications/${encodeURIComponent(id)}`)
    ),
  createPrequalification: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/prequalifications', payload)),
  continueEoi: (id: string, invitationKind: 'RFQ' | 'RFT') =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/prequalifications/${encodeURIComponent(id)}/continue`, {
        invitationKind,
      })
    ),
  patchPrequalification: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/prequalifications/${encodeURIComponent(id)}`, payload)
    ),

  listTenders: (opportunityId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/tenders', {
        params: opportunityId ? { opportunityId } : {},
      })
    ),
  getTender: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`/epc/commercial/tenders/${encodeURIComponent(id)}`)),
  getTenderWorkspace: (id: string) =>
    unwrap<EpcTenderWorkspace>(
      api.get(`/epc/commercial/tenders/${encodeURIComponent(id)}/workspace`)
    ),
  createTender: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/tenders', payload)),
  patchTender: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/tenders/${encodeURIComponent(id)}`, payload)
    ),

  listBidDecisions: () =>
    unwrap<Record<string, unknown>[]>(api.get('/epc/commercial/bid-decisions')),
  getBidDecision: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/bid-decisions/${encodeURIComponent(id)}`)
    ),
  createBidDecision: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/bid-decisions', payload)),
  patchBidDecision: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/bid-decisions/${encodeURIComponent(id)}`, payload)
    ),
  submitBidDecision: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/bid-decisions/${encodeURIComponent(id)}/submit`)
    ),

  listProposals: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/proposals', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  getProposal: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/proposals/${encodeURIComponent(id)}`)
    ),
  createProposal: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/proposals', payload)),
  patchProposal: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/proposals/${encodeURIComponent(id)}`, payload)
    ),
  getProposalSubmissionBasis: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/proposals/${encodeURIComponent(id)}/submission-basis`)
    ),
  certifyProposalEstimateBasis: (
    id: string,
    payload: {
      estimate_id: string;
      estimate_revision_id: string;
      replace?: boolean;
    }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(
        `/epc/commercial/proposals/${encodeURIComponent(id)}/certify-estimate-basis`,
        payload
      )
    ),
  certifySubmissionEstimateBasis: (
    id: string,
    payload: {
      estimate_id: string;
      estimate_revision_id: string;
      replace?: boolean;
    }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(
        `/epc/commercial/submissions/${encodeURIComponent(id)}/certify-estimate-basis`,
        payload
      )
    ),
  acknowledgeAddendumNoEstimateImpact: (id: string, reason: string) =>
    unwrap<Record<string, unknown>>(
      api.post(
        `/epc/commercial/addenda/${encodeURIComponent(id)}/acknowledge-no-estimate-impact`,
        { reason }
      )
    ),

  listSubmissions: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/submissions', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  getSubmission: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/submissions/${encodeURIComponent(id)}`)
    ),
  createSubmission: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/submissions', payload)),
  patchSubmission: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/submissions/${encodeURIComponent(id)}`, payload)
    ),
  submitSubmission: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/submissions/${encodeURIComponent(id)}/submit`)
    ),

  listClarifications: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/clarifications', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  createClarification: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/clarifications', payload)),
  patchClarification: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/clarifications/${encodeURIComponent(id)}`, payload)
    ),

  listAddenda: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/addenda', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  createAddendum: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/addenda', payload)),
  patchAddendum: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/addenda/${encodeURIComponent(id)}`, payload)
    ),

  listNegotiations: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/negotiations', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  createNegotiation: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/negotiations', payload)),

  listAwards: (tenderId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/awards', {
        params: tenderId ? { tenderId } : {},
      })
    ),
  getAward: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`/epc/commercial/awards/${encodeURIComponent(id)}`)),
  createAward: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/awards', payload)),
  patchAward: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/awards/${encodeURIComponent(id)}`, payload)
    ),
  submitAward: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/awards/${encodeURIComponent(id)}/submit`)
    ),
  reconcileAward: (awardId: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/awards/${encodeURIComponent(awardId)}/reconcile`, { awardId })
    ),
  getAwardEstimateBasis: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/awards/${encodeURIComponent(id)}/estimate-basis`)
    ),
  setAwardEstimateBasis: (
    id: string,
    payload: {
      estimateRevisionId: string;
      expectedSubmissionId?: string;
      replace?: boolean;
      reason?: string;
    }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/awards/${encodeURIComponent(id)}/estimate-basis`, payload)
    ),
  setAwardCostImpact: (
    id: string,
    payload: { decision: string; reason: string; classification?: string }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/awards/${encodeURIComponent(id)}/cost-impact`, payload)
    ),
  listReconciliations: (awardId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/reconciliations', {
        params: awardId ? { awardId } : {},
      })
    ),

  listContracts: () => unwrap<Record<string, unknown>[]>(api.get('/epc/commercial/contracts')),
  getContract: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/contracts/${encodeURIComponent(id)}`)
    ),
  createContractFromAward: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/contracts/from-award', payload)),
  createDirectContract: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/epc/commercial/contracts', payload)),
  getContractEstimateBasis: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`/epc/commercial/contracts/${encodeURIComponent(id)}/estimate-basis`)
    ),
  linkContractEstimate: (id: string, payload: { estimateRevisionId: string; replace?: boolean }) =>
    unwrap<Record<string, unknown>>(
      api.put(`/epc/commercial/contracts/${encodeURIComponent(id)}/estimate-basis`, payload)
    ),
  unlinkContractEstimate: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.delete(`/epc/commercial/contracts/${encodeURIComponent(id)}/estimate-basis`)
    ),
  patchContract: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`/epc/commercial/contracts/${encodeURIComponent(id)}`, payload)
    ),
  submitContract: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/contracts/${encodeURIComponent(id)}/submit`)
    ),
  activateContract: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/epc/commercial/contracts/${encodeURIComponent(id)}/activate`)
    ),

  listMobilizations: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get('/epc/commercial/mobilizations', {
        params: contractId ? { contractId } : {},
      })
    ),
  getMobilization: (id: string) =>
    unwrap<Record<string, unknown> & { items?: Record<string, unknown>[] }>(
      api.get(`/epc/commercial/mobilizations/${encodeURIComponent(id)}`)
    ),
  createMobilization: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown> & { items?: Record<string, unknown>[] }>(
      api.post('/epc/commercial/mobilizations', payload)
    ),
  patchMobilizationItem: (
    mobilizationId: string,
    itemId: string,
    payload: Record<string, unknown>
  ) =>
    unwrap<Record<string, unknown> & { items?: Record<string, unknown>[] }>(
      api.patch(
        `/epc/commercial/mobilizations/${encodeURIComponent(mobilizationId)}/items/${encodeURIComponent(itemId)}`,
        payload
      )
    ),
  submitMobilization: (id: string) =>
    unwrap<Record<string, unknown> & { items?: Record<string, unknown>[] }>(
      api.post(`/epc/commercial/mobilizations/${encodeURIComponent(id)}/submit`)
    ),
  activateProjectFromMobilization: (id: string) =>
    unwrap<Record<string, unknown> & { items?: Record<string, unknown>[] }>(
      api.post(`/epc/commercial/mobilizations/${encodeURIComponent(id)}/activate-project`)
    ),

  convertOpportunityToTender: (payload: {
    opportunityId: string;
    prequalificationId?: string;
    title?: string;
    tenderNumber?: string;
    invitationKind?: 'RFQ' | 'RFT';
  }) =>
    unwrap<Record<string, unknown>>(
      api.post('/epc/commercial/conversions/opportunity-to-tender', {
        opportunityId: payload.opportunityId,
        prequalificationId: payload.prequalificationId,
        title: payload.title,
        tenderNumber: payload.tenderNumber,
        invitationKind: payload.invitationKind,
      })
    ),
};
